import { useEffect, useState } from "react";
import { ChevronRight, Search, UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { supabase } from "../../../lib/supabase";

interface VincerBaseUser {
  name: string;
  email: string;
}

interface MentorData {
  uuid: string;
  vincer_base_user: VincerBaseUser;
}

interface MenteeData {
  uuid: string;
  vincer_base_user: VincerBaseUser;
}

interface RelationshipAssignment {
  uuid: string;
  start: string;
  end: string | null;
  mentor: MentorData;
  mentee: MenteeData;
}

interface Assignment {
  uuid: string;
  start: string;
  end: string | null;
  mentor: {
    name: string;
    email: string;
  };
  mentee: {
    name: string;
    email: string;
  };
}

export function AssignmentsList() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAssignments() {
      try {
        const { data, error: fetchError } = await supabase
          .from("relationship_assignments")
          .select(
            `
            uuid,
            start,
            end,
            mentor:mentor_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            ),
            mentee:mentee_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            )
          `
          )
          .order("start", { ascending: false });

        if (fetchError) throw fetchError;

        if (data) {
          const formattedAssignments = (
            data as unknown as RelationshipAssignment[]
          ).map((assignment) => ({
            uuid: assignment.uuid,
            mentor: {
              name: assignment.mentor.vincer_base_user.name,
              email: assignment.mentor.vincer_base_user.email,
            },
            mentee: {
              name: assignment.mentee.vincer_base_user.name,
              email: assignment.mentee.vincer_base_user.email,
            },
            start: assignment.start,
            end: assignment.end,
          }));
          setAssignments(formattedAssignments);
        }
      } catch (err) {
        console.error("Error fetching assignments:", err);
        setError("Failed to load assignments");
      } finally {
        setLoading(false);
      }
    }

    fetchAssignments();
  }, []);

  const filteredAssignments = assignments.filter((assignment) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      assignment.mentor.name.toLowerCase().includes(searchLower) ||
      assignment.mentor.email.toLowerCase().includes(searchLower) ||
      assignment.mentee.name.toLowerCase().includes(searchLower) ||
      assignment.mentee.email.toLowerCase().includes(searchLower)
    );
  });

  const getStatus = (assignment: Assignment) => {
    if (assignment.end) return "completed";
    return "active";
  };

  const getStatusColor = (status: "active" | "completed") => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Mentorship Assignments</h1>
        <button
          onClick={() => navigate("/coordinator/assignments/new")}
          className="flex items-center gap-2 rounded-lg bg-[#4CAF50] px-4 py-2 text-white transition-colors hover:bg-[#45a049]"
        >
          <UserPlus className="size-5" />
          <span>New Assignment</span>
        </button>
      </div>

      <div className="mb-6 rounded-xl border bg-white shadow-sm">
        <div className="border-b p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search assignments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border py-2 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#ffcc32]"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-b-2 border-[#ffcc32]"></div>
            <p className="text-gray-600">Loading assignments...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        ) : filteredAssignments.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600">No assignments found</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredAssignments.map((assignment) => {
              const status = getStatus(assignment);
              return (
                <div
                  key={assignment.uuid}
                  className="cursor-pointer p-4 transition-colors hover:bg-gray-50"
                  onClick={() =>
                    navigate(`/coordinator/assignments/${assignment.uuid}`)
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium">
                          {assignment.mentor.name}
                        </h3>
                        <ChevronRight className="size-4 text-gray-400" />
                        <h3 className="font-medium">
                          {assignment.mentee.name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{assignment.mentor.email}</span>
                        <span>→</span>
                        <span>{assignment.mentee.email}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        Started:{" "}
                        {new Date(assignment.start).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span
                        className={`rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(status)}`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                      <ChevronRight className="size-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
